import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Logo from './Logo';

const StyledHeader = styled.header`
  background-color: var(--color-white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  
  &.scrolled {
    padding: 0.5rem 0;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    display: ${props => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--color-white);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
    padding: 1rem 0;
  }
  
  @media (min-width: 769px) {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  color: var(--color-primary);
  margin: 0 1rem;
  font-weight: 600;
  padding: 0.5rem;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 3px;
    bottom: 0;
    left: 0;
    background-color: var(--color-accent);
    transition: width 0.3s ease;
  }
  
  &:hover:after,
  &.active:after {
    width: 100%;
  }
  
  @media (max-width: 768px) {
    margin: 0.5rem 2rem;
  }
`;

const SSOButton = styled.a`
  background-color: var(--color-accent);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-left: 1rem;
  font-family: 'Montserrat', sans-serif;
  
  &:hover {
    background-color: var(--color-primary-dark);
    color: white;
  }
  
  @media (max-width: 768px) {
    margin: 1rem 2rem;
    text-align: center;
  }
`;

const SupportButton = styled.button`
  background-color: var(--color-primary);
  color: white;
  border: 0;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  margin-left: 1rem;
  font-family: 'Montserrat', sans-serif;

  &:hover {
    background-color: var(--color-primary-dark);
  }

  @media (max-width: 768px) {
    margin: 1rem 2rem;
  }
`;

const SupportForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin: 1rem 2rem;
  }
`;

const SupportInput = styled.input`
  width: 140px;
  padding: 0.45rem;
  border: 1px solid #c8cdd3;
  border-radius: 4px;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  color: var(--color-primary);
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSupportLoginOpen, setIsSupportLoginOpen] = useState(false);
  const [supportUsername, setSupportUsername] = useState('');
  const [supportPassword, setSupportPassword] = useState('');
  const [supportError, setSupportError] = useState('');
  const location = useLocation();

  const handleSupportLogin = async (event) => {
    event.preventDefault();
    setSupportError('');

    try {
      const response = await fetch('/api/zendesk-jwt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: supportUsername, password: supportPassword })
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Unable to sign in');
      if (typeof window.zE !== 'function') throw new Error('Zendesk widget is not ready yet');

      window.zE('messenger', 'loginUser', (callback) => callback(data.jwt), (error) => {
        if (error) {
          setSupportError('Zendesk sign-in failed');
          return;
        }
        if (data.metadata) {
          window.zE('messenger:set', 'conversationFields', [
            { id: '55520120138003', value: data.metadata }
          ], () => {
            setIsSupportLoginOpen(false);
            setSupportPassword('');
            window.zE('messenger', 'show');
            window.zE('messenger', 'open');
          });
        } else {
          setIsSupportLoginOpen(false);
          setSupportPassword('');
          window.zE('messenger', 'show');
          window.zE('messenger', 'open');
        }
      });
    } catch (error) {
      setSupportError(error.message);
    }
  };
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <StyledHeader className={isScrolled ? 'scrolled' : ''}>
      <HeaderContainer>
        <Logo />
        
        <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
        
        <Nav isOpen={isMobileMenuOpen}>
          <NavLink 
            to="/" 
            className={location.pathname === '/' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Home
          </NavLink>
          <NavLink 
            to="/about" 
            className={location.pathname === '/about' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            About
          </NavLink>
          <NavLink 
            to="/services" 
            className={location.pathname === '/services' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Services
          </NavLink>
          <NavLink 
            to="/projects" 
            className={location.pathname === '/projects' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Projects
          </NavLink>
          <NavLink 
            to="/tech-stack" 
            className={location.pathname === '/tech-stack' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Tech Stack
          </NavLink>
          <NavLink 
            to="/contact" 
            className={location.pathname === '/contact' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Contact
          </NavLink>
          
          <SSOButton 
            href="https://dev-84918634.okta.com/app/zendesk/exkt89ftiX6QbfOxz5d6/sso/saml"
            target="_blank"
            rel="noopener noreferrer"
          >
            SSO Sign In
          </SSOButton>

          {!isSupportLoginOpen ? (
            <SupportButton type="button" onClick={() => setIsSupportLoginOpen(true)}>
              Support Login
            </SupportButton>
          ) : (
            <SupportForm onSubmit={handleSupportLogin}>
              <SupportInput
                type="text"
                placeholder="Username"
                aria-label="Support username"
                value={supportUsername}
                onChange={(event) => setSupportUsername(event.target.value)}
                required
              />
              <SupportInput
                type="password"
                placeholder="Password"
                aria-label="Support password"
                value={supportPassword}
                onChange={(event) => setSupportPassword(event.target.value)}
                required
              />
              <SupportButton type="submit">Open Chat</SupportButton>
              {supportError && <span role="alert">{supportError}</span>}
            </SupportForm>
          )}
        </Nav>
      </HeaderContainer>
    </StyledHeader>
  );
};

export default Header;
